@if (customer(); as c) {
  <div class="flex flex-col gap-4">
    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-info-circle text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Información general</span>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div class="sm:col-span-2">
            <p class="text-xs font-medium text-slate-500">Razón social</p>
            <p class="text-sm font-semibold text-slate-900">{{ c.legalName || '—' }}</p>
            @if (c.comercialName) {
              <p class="text-xs text-slate-500 mt-0.5">{{ c.comercialName }}</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">País</p>
            <p class="text-sm text-slate-700">{{ c.country?.name || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Tipo</p>
            <p-tag severity="secondary" class="text-xs" [value]="getCompanyTypeLabel(c.type)" />
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">RFC</p>
            <p class="text-sm text-slate-700 font-mono">{{ c.taxId || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Email</p>
            @if (c.email) {
              <a [href]="'mailto:' + c.email" class="text-sm text-sky-600 hover:text-sky-800">{{ c.email }}</a>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Teléfono</p>
            @if (c.phone) {
              <a [href]="'tel:' + c.phone" class="text-sm text-sky-600 hover:text-sky-800">{{ c.phone }}</a>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Plazo de pago</p>
            @if (c.daysUntilExpiry != null) {
              <p class="text-sm text-slate-700">{{ c.daysUntilExpiry }} día{{ c.daysUntilExpiry === 1 ? '' : 's' }}</p>
            } @else {
              <p class="text-sm text-slate-700">—</p>
            }
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Fecha de creación</p>
            <p class="text-sm text-slate-700">{{ c.createdAt ? (c.createdAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Última actualización</p>
            <p class="text-sm text-slate-700">{{ c.updatedAt ? (c.updatedAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
          <i class="pi pi-map-marker text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Direcciones ({{ c.addresses?.length ?? 0 }})</span>
      </div>
      @if (c.addresses?.length) {
        <div class="divide-y divide-slate-100">
          @for (address of c.addresses; track address.id) {
            <div class="flex items-start gap-2 px-4 py-2">
              <div class="min-w-0 flex-1">
                <span class="text-[11px] font-semibold text-slate-600 uppercase">{{ address.type || 'Dirección' }}</span>
                <p class="text-xs text-slate-900 leading-snug mt-0.5">
                  {{ address.street || '—' }}
                  @if (address.externalNo) { · Ext. {{ address.externalNo }} }
                  @if (address.internalNo) { · Int. {{ address.internalNo }} }
                  · {{ address.colonia || '—' }}, {{ address.municipality || '—' }} {{ address.zipCode || '' }}
                  · {{ address.country?.name || '—' }}
                </p>
              </div>
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-slate-500 px-4 py-3">Sin direcciones registradas.</p>
      }
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
          <i class="pi pi-users text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Contactos ({{ c.contacts?.length ?? 0 }})</span>
      </div>
      @if (c.contacts?.length) {
        <div class="divide-y divide-slate-100">
          @for (contact of c.contacts; track contact.id) {
            <div class="flex items-start gap-2 px-4 py-2">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <span class="text-xs font-medium text-slate-900 truncate">{{ contact.conactName || '—' }}</span>
                  <span [class]="getContactTypeBadgeClasses(contact.type)">
                    {{ getContactTypeLabel(contact.type) }}
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  @if (contact.email) {
                    <a
                      [href]="'mailto:' + contact.email"
                      class="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800">
                      <i class="pi pi-envelope text-[10px] shrink-0"></i>
                      <span class="truncate">{{ contact.email }}</span>
                    </a>
                  }
                  @if (contact.phone) {
                    <a
                      [href]="'tel:' + contact.phone"
                      class="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800">
                      <i class="pi pi-phone text-[10px] shrink-0"></i>
                      <span>{{ contact.phone }}</span>
                    </a>
                  }
                  @if (!contact.email && !contact.phone) {
                    <span class="text-xs text-slate-500">—</span>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-slate-500 px-4 py-3">Sin contactos registrados.</p>
      }
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-shopping-cart text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Ventas recientes</span>
      </div>
      @if (salesLoading()) {
        <div class="flex flex-col items-center justify-center py-8 gap-3">
          <i class="pi pi-spin pi-spinner text-2xl text-sky-500"></i>
          <p class="text-sm text-slate-500">Cargando ventas...</p>
        </div>
      } @else if (sales().length === 0) {
        <p class="text-sm text-slate-500 px-4 py-3">Este cliente aún no tiene ventas registradas.</p>
      } @else {
        <div class="divide-y divide-slate-100">
          @for (sale of sales(); track sale.id) {
            <div class="flex items-center gap-2 px-4 py-2">
                <div class="shrink-0 flex items-center justify-center [&>i]:text-xs"
                  [class]="getStatusIconClasses(sale.status, true)">
                  <i [class]="getStatusIcon(sale.status)"></i>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-xs font-semibold text-slate-900 truncate">{{ sale.number || '—' }}</span>
                    <span class="text-[11px] text-slate-500 shrink-0">
                      {{ sale.createdAt ? (sale.createdAt | date:'dd/MM/yyyy') : '—' }}
                    </span>
                  </div>
                  <div class="flex flex-wrap gap-1 mt-0.5">
                    <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getStatusLabel(sale.status) }}</span>
                    @if (sale.processStatus) {
                      <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getPipelineStatusLabel(sale.processStatus) }}</span>
                    }
                    @if (sale.saleType) {
                      <span class="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{{ getSaleTypeLabel(sale.saleType) }}</span>
                    }
                  </div>
                </div>
                <span class="text-xs font-bold text-slate-900 shrink-0 tabular-nums">
                  {{ (sale.total ?? 0) | currency:(sale.currency || 'MXN'):'symbol':'1.2-2' }}
                </span>
                @if (sale.id != null) {
                  <button
                    type="button"
                    class="shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-sky-600 hover:bg-sky-50 hover:text-sky-800"
                    title="Ver venta"
                    (click)="openSaleDetails(sale)">
                    <i class="pi pi-eye text-xs"></i>
                  </button>
                }
              </div>
          }
        </div>

        @if (salesHasMore()) {
          <div class="flex justify-center py-2 border-t border-slate-100">
            <p-button
              label="Cargar más"
              icon="pi pi-plus"
              severity="secondary"
              [outlined]="true"
              size="small"
              [loading]="salesLoadingMore()"
              (onClick)="loadMoreSales()" />
          </div>
        }
      }
    </div>

    @if (canListMandates) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center justify-between gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <div class="flex items-center gap-2">
            <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
              <i class="pi pi-credit-card text-sm"></i>
            </span>
            <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Cobros bajo demanda</span>
          </div>
          @if (canCreateMandate && customer().id != null) {
            <a [routerLink]="['/mit-mandates/nuevo']" [queryParams]="{ customerId: customer().id }"
              class="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900">
              <i class="pi pi-plus text-[10px]"></i> Nuevo mandato
            </a>
          }
        </div>
        @if (mandatesLoading()) {
          <div class="flex justify-center py-4"><i class="pi pi-spin pi-spinner text-lg text-sky-500"></i></div>
        } @else if (mandates().length === 0) {
          <p class="text-sm text-slate-500 px-4 py-3">Este cliente no tiene mandatos de cobro bajo demanda.</p>
        } @else {
          <div class="divide-y divide-slate-100">
            @for (mandate of mandates(); track mandate.id) {
              <a [routerLink]="['/mit-mandates', mandate.id]" class="flex items-center gap-2 px-4 py-2 hover:bg-sky-50/50">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-xs font-semibold text-slate-900">{{ mandate.number }}</span>
                    <p-tag class="!text-[10px]" [severity]="mandateSeverity(mandate.status)" [value]="mandateStatusLabel(mandate.status)" />
                  </div>
                  <span class="text-[11px] text-slate-500">
                    {{ mandate.card?.lastDigits ? '•••• ' + mandate.card?.lastDigits : 'Sin tarjeta' }}
                  </span>
                </div>
                <span class="text-xs text-slate-700 shrink-0 tabular-nums">
                  {{ mandate.limits?.maxAmountPerMonth | currency:(mandate.currencyCode || 'MXN'):'symbol-narrow':'1.2-2' }}/mes
                </span>
              </a>
            }
          </div>
        }
      </div>
    }
  </div>
}
